import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    Alert, Modal, PrimaryButton, SecondaryButton, CheckboxInput,
} from '@luminalearning/components';
import { LuminaQualification } from '@luminalearning/types';
import { ProjectCardProject } from 'src/Components/Developer/ProjectListSection';
import { qualificationTabClass, qualificationTabLabel } from 'src/Components/Developer/qualificationTabUtils';

type Props = {
    isOpen: boolean;
    setOpen: (isOpen: boolean) => void;
    project: ProjectCardProject | null;
    productQualifications: LuminaQualification[] | null | undefined;
    onSubmit: (values: {
        projectId: number;
        productIds: number[];
    }) => Promise<string | undefined>;
    isSubmitting?: boolean;
};

const EditTestProjectModal = ({
    isOpen, setOpen, project, productQualifications, onSubmit, isSubmitting,
}: Props) => {
    const intl = useIntl();

    const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
    const [activeQualificationTab, setActiveQualificationTab] = useState(0);
    const [submitStatus, setSubmitStatus] = useState<{ tone: 'Success' | 'Error'; message: string } | null>(null);

    // Pre-populate selected products when project changes or modal opens
    useEffect(() => {
        if (project && isOpen) {
            setSelectedProductIds(
                (project.enabledProducts ?? [])
                    .map((p) => p.id)
                    .filter((id): id is number => id != null)
            );
            setSubmitStatus(null);
        }
    }, [project, isOpen]);

    const toggleProduct = (productId: number) => {
        setSelectedProductIds((prev) => (prev.includes(productId)
            ? prev.filter((id) => id !== productId)
            : [...prev, productId]));
    };

    const handleSubmit = async () => {
        if (!project) {
            return;
        }
        setSubmitStatus(null);
        const response = await onSubmit({
            projectId: project.id,
            productIds: selectedProductIds,
        });
        if (response === 'SUCCESS') {
            setSubmitStatus({
                tone: 'Success',
                message: intl.formatMessage({
                    id: 'LP_DEVELOPER_EDIT_PROJECT_SUCCESS',
                    defaultMessage: 'Project updated successfully.',
                }),
            });
        } else if (response === 'BAD_REQUEST') {
            setSubmitStatus({
                tone: 'Error',
                message: intl.formatMessage({
                    id: 'LP_DEVELOPER_EDIT_PROJECT_BAD_REQUEST',
                    defaultMessage: 'Invalid request. Please check your inputs and try again.',
                }),
            });
        } else if (response === 'UNAUTHORIZED') {
            setSubmitStatus({
                tone: 'Error',
                message: intl.formatMessage({
                    id: 'LP_DEVELOPER_EDIT_PROJECT_UNAUTHORIZED',
                    defaultMessage: 'You are not authorized to update this project.',
                }),
            });
        } else {
            setSubmitStatus({
                tone: 'Error',
                message: intl.formatMessage({
                    id: 'LP_DEVELOPER_EDIT_PROJECT_SERVER_ERROR',
                    defaultMessage: 'Something went wrong. Please try again later.',
                }),
            });
        }
    };

    const handleClose = () => {
        setSelectedProductIds([]);
        setActiveQualificationTab(0);
        setSubmitStatus(null);
        setOpen(false);
    };

    const activeQualification = productQualifications?.[activeQualificationTab] ?? null;

    return (
        <Modal
            className='w-70'
            isOpen={isOpen}
            setOpen={handleClose}
            children={
                <div className='flex flex-col space-y-6 overflow-y-auto pb-3'>
                    {project && (
                        <div className='text-headline-reg font-bold'>{project.name}</div>
                    )}

                    {productQualifications && productQualifications.length > 0 && (
                        <div className='flex flex-col gap-y-4'>
                            <span className='text-label-reg font-semibold'>
                                <FormattedMessage
                                    id="LP_DEVELOPER_ENABLED_PRODUCTS_LABEL"
                                    defaultMessage="Enabled Products"
                                />
                            </span>
                            <div className='flex flex-row flex-wrap gap-2' role='tablist'>
                                {productQualifications.map((qual, index) => (
                                    activeQualificationTab === index ? (
                                        <PrimaryButton
                                            key={qual.qualificationName}
                                            type='button'
                                            text={qualificationTabLabel(qual.qualificationName)}
                                            onClick={() => setActiveQualificationTab(index)}
                                            className={qualificationTabClass}
                                        />
                                    ) : (
                                        <SecondaryButton
                                            key={qual.qualificationName}
                                            type='button'
                                            text={qualificationTabLabel(qual.qualificationName)}
                                            onClick={() => setActiveQualificationTab(index)}
                                            className={qualificationTabClass}
                                        />
                                    )
                                ))}
                            </div>
                            {activeQualification && (
                                <div className='grid grid-cols-4 gap-y-2 gap-x-4'>
                                    {(activeQualification.products ?? []).filter((p) => p.name).map((product) => (
                                        <div key={product.id} className='flex flex-row items-center gap-x-2'>
                                            <CheckboxInput
                                                inputId={`edit-product-${product.id}`}
                                                isChecked={selectedProductIds.includes(product.id)}
                                                setChecked={() => toggleProduct(product.id)}
                                                isDisabled={isSubmitting}
                                                className='w-8'
                                            />
                                            <label
                                                htmlFor={`edit-product-${product.id}`}
                                                style={{ color: product.colourTheme ?? undefined }}
                                            >
                                                {product.name}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {submitStatus && (
                        <Alert type={submitStatus.tone} message={submitStatus.message} />
                    )}

                    <div className='flex flex-row justify-end space-x-4'>
                        <SecondaryButton
                            type='button'
                            text={intl.formatMessage({
                                id: 'LP_CANCEL',
                                defaultMessage: 'Cancel',
                            })}
                            onClick={handleClose}
                            isDisabled={isSubmitting}
                        />
                        <PrimaryButton
                            type='button'
                            text={intl.formatMessage({
                                id: 'LP_SAVE',
                                defaultMessage: 'Save',
                            })}
                            onClick={handleSubmit}
                            isDisabled={isSubmitting}
                            isLoading={isSubmitting}
                        />
                    </div>
                </div>
            }
        />
    );
};

export default EditTestProjectModal;
